'use strict';

/**
 * Configuration object for autocomplete container styling and attributes
 */
const AUTOCOMPLETE_CONFIG = {
    classes: {
        container: 'cc-form__autocomplete-suggestions js-suggestions-container',
        label: 'cc-visibility-hidden',
        liveRegion: 'cc-visibility-hidden'
    },
    roles: {
        listbox: 'listbox',
        combobox: 'combobox'
    },
    styles: {
        position: 'absolute',
        top: '100%',
        left: '0',
        right: '0',
        background: 'var(--color-white)',
        border: '0.1rem solid var(--color-grey3)',
        borderTop: 'none',
        maxHeight: '20rem',
        overflowY: 'auto',
        zIndex: 2,
        display: 'none',
        listStyle: 'none',
        margin: 0,
        padding: 0
    }
};

/**
 * Creates an accessible autocomplete suggestions container with proper ARIA attributes
 * @param {jQuery} $container - The parent container where the suggestions will be appended
 * @param {jQuery} $input - The input element that will control the autocomplete
 * @param {Object} options - Configuration options for the container
 * @param {string} [options.labelText] - Text for the accessibility label (fallback if no data attribute)
 * @param {string} [options.containerClass] - Additional CSS class for the container
 * @param {Object} [options.customStyles] - Custom CSS styles to override defaults
 * @returns {Object} Object containing the created elements and their IDs
 */
function createAutocompleteContainer($container, $input, options = {}) {
    const labelText = $container.attr('data-autocomplete-label') || options.labelText;
    
    const settings = {
        labelText: labelText,
        containerClass: options.containerClass || '',
        customStyles: options.customStyles || {}
    };

    // Generate unique IDs
    const timestamp = Date.now();
    const listboxId = `autocomplete-listbox-${timestamp}`;
    const labelId = `autocomplete-label-${timestamp}`;

    // Create accessibility label
    const $label = $('<span>', {
        id: labelId,
        class: AUTOCOMPLETE_CONFIG.classes.label,
        text: settings.labelText
    });

    // Merge default styles with custom styles
    const containerStyles = { ...AUTOCOMPLETE_CONFIG.styles, ...settings.customStyles };

    // Create suggestions container
    const $suggestionsContainer = $('<ul>', {
        id: listboxId,
        class: `${AUTOCOMPLETE_CONFIG.classes.container} ${settings.containerClass}`.trim(),
        role: AUTOCOMPLETE_CONFIG.roles.listbox,
        'aria-labelledby': labelId,
        tabindex: '-1',
        css: containerStyles
    });

    // Append elements to container
    $container.append($label).append($suggestionsContainer);

    // Configure input ARIA attributes
    $input.attr({
        'role': AUTOCOMPLETE_CONFIG.roles.combobox,
        'aria-expanded': 'false',
        'aria-autocomplete': 'list',
        'aria-owns': listboxId,
        'aria-describedby': labelId
    });

    return {
        $container: $suggestionsContainer,
        $label: $label,
        listboxId: listboxId,
        labelId: labelId
    };
}

/**
 * Creates a live region for screen reader announcements
 * @param {jQuery} $container - The parent container where the live region will be appended
 * @param {Object} options - Configuration options for the live region
 * @param {string} [options.politeness='polite'] - ARIA live politeness setting ('polite' or 'assertive')
 * @param {boolean} [options.atomic=true] - Whether the entire region should be announced as a whole
 * @returns {jQuery} The created live region element
 */
function createLiveRegion($container, options = {}) {
    const settings = {
        politeness: options.politeness || 'polite',
        atomic: options.atomic !== false
    };

    const $liveRegion = $('<div>', {
        class: AUTOCOMPLETE_CONFIG.classes.liveRegion,
        'aria-live': settings.politeness,
        'aria-atomic': settings.atomic.toString()
    });

    $container.append($liveRegion);
    return $liveRegion;
}

/**
 * Updates ARIA attributes for autocomplete state changes
 * @param {jQuery} $input - The input element
 * @param {Object} state - The current state of the autocomplete
 * @param {boolean} state.expanded - Whether the suggestions are visible
 * @param {string} [state.activeDescendant] - ID of the currently active suggestion
 */
function updateAutocompleteState($input, state) {
    $input.attr('aria-expanded', state.expanded.toString());
    
    if (state.activeDescendant) {
        $input.attr('aria-activedescendant', state.activeDescendant);
    } else {
        $input.removeAttr('aria-activedescendant');
    }
}

/**
 * Announces results to screen readers via live region
 * @param {jQuery} $liveRegion - The live region element
 * @param {number} count - Number of suggestions available
 * @param {Object} options - Configuration options for the announcement
 * @param {string} [options.noResultsText='Nessun suggerimento disponibile'] - Text when no results
 * @param {string} [options.singleResultText='suggerimento disponibile'] - Text for single result
 * @param {string} [options.multipleResultsText='suggerimenti disponibili'] - Text for multiple results
 * @param {string} [options.instructionText='Usa le frecce per navigare.'] - Navigation instructions
 */
function announceResults($liveRegion, count, options = {}) {
    const $container = options.$container || $liveRegion.closest('[data-autocomplete-no-results]');
    
    const settings = {
        noResultsText: $container.attr('data-autocomplete-no-results') || options.noResultsText,
        singleResultText: $container.attr('data-autocomplete-single-result') || options.singleResultText,
        multipleResultsText: $container.attr('data-autocomplete-multiple-results') || options.multipleResultsText,
        instructionText: $container.attr('data-autocomplete-instructions') || options.instructionText,
    };

    let message;
    if (count === 0) {
        message = settings.noResultsText;
    } else if (count === 1) {
        message = `${count} ${settings.singleResultText}. ${settings.instructionText}`;
    } else {
        message = `${count} ${settings.multipleResultsText}. ${settings.instructionText}`;
    }

    $liveRegion.text(message);
}

/**
 * Creates a suggestion item element with proper ARIA attributes and event handlers
 * @param {Object} suggestion - The suggestion data object
 * @param {number} index - The index of the suggestion in the list
 * @param {Object} handlers - Event handler functions
 * @param {Function} handlers.onSelect - Function called when suggestion is selected
 * @param {Function} handlers.onHover - Function called when suggestion is hovered
 * @param {Object} options - Configuration options
 * @param {string} [options.itemClass='cc-suggestion-item'] - CSS class for the suggestion item
 * @param {string} [options.role='option'] - ARIA role for the suggestion item
 * @param {string} [options.idPrefix='autocomplete-option'] - Prefix for the suggestion item ID
 * @param {Function} [options.textExtractor] - Function to extract display text from suggestion
 * @returns {Object} Object containing the created element and its ID
 */
function createSuggestionItem(suggestion, index, handlers, options = {}) {
    const settings = {
        itemClass: options.itemClass || 'cc-suggestion-item',
        role: options.role || 'option',
        idPrefix: options.idPrefix || 'autocomplete-option',
        textExtractor: options.textExtractor || ((suggestion) => suggestion.placePrediction?.text?.text || suggestion.text || '')
    };

    // Generate unique ID for the suggestion item
    const optionId = `${settings.idPrefix}-${Date.now()}-${index}`;
    
    // Extract display text using the provided extractor function
    const displayText = settings.textExtractor(suggestion);

    // Create the suggestion item element
    const $suggestionItem = $('<li>', {
        id: optionId,
        class: settings.itemClass,
        role: settings.role,
        'aria-selected': 'false',
        text: displayText
    });

    // Add click handler
    if (handlers.onSelect) {
        $suggestionItem.on('click', () => {
            handlers.onSelect(suggestion, displayText);
        });
    }

    // Add hover handler for accessibility
    if (handlers.onHover) {
        $suggestionItem.on('mouseenter', () => {
            handlers.onHover($suggestionItem, optionId);
        });
    }

    return {
        $element: $suggestionItem,
        id: optionId
    };
}

/**
 * Creates multiple suggestion items and appends them to a container
 * @param {Array} suggestions - Array of suggestion objects
 * @param {jQuery} $container - Container element to append suggestions to
 * @param {Object} handlers - Event handler functions
 * @param {Function} handlers.onSelect - Function called when suggestion is selected
 * @param {Function} handlers.onHover - Function called when suggestion is hovered
 * @param {Object} options - Configuration options (same as createSuggestionItem)
 * @returns {Array} Array of created suggestion item objects
 */
function createSuggestionItems(suggestions, $container, handlers, options = {}) {
    const createdItems = [];

    suggestions.forEach((suggestion, index) => {
        const suggestionItem = createSuggestionItem(suggestion, index, handlers, options);
        $container.append(suggestionItem.$element);
        createdItems.push(suggestionItem);
    });

    return createdItems;
}

/**
 * Extracts Google Maps API key from loaded script tags
 * @returns {string|null} The extracted API key or null if not found
 */
function extractGoogleMapsApiKey() {
    let googleApisAutocomplete = null;
    
    $('script').each(function() {
        var src = $(this).attr('src');
        if (src && src.includes("maps.googleapis.com")) {
            var apiKeyIndex = src.indexOf("key=");
            if (apiKeyIndex !== -1) {
                googleApisAutocomplete = src.substring(apiKeyIndex + 4).split("&")[0];
                return false; // Break out of the loop
            }
        }
    });
    
    return googleApisAutocomplete;
}

/**
 * Validates if the Google Maps API key is valid for Places Autocomplete
 * @param {string} apiKey - The API key to validate
 * @param {Function} callback - Callback function that receives validation result
 * @param {boolean} callback.isValid - Whether the API key is valid
 */
async function isGoogleApiKeyValid(apiKey, callback) {
    try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ 
            input: 'test' 
        });
        let place = suggestions && suggestions.length > 0 ? suggestions[0].placePrediction?.toPlace() : false;
        
        if (place) {
            callback(true); // API Key is valid
        } else {
            callback(false); // API Key is not valid
        }
    } catch (error) {
        console.error('Error validating Google Maps API key:', error);
        callback(false); // API Key is not valid
    }
}

/**
 * Retrieves a Google Places object from a place prediction with specified fields
 * @param {Object} placePrediction - The Google Maps place prediction object
 * @param {Object} options - Configuration options for field retrieval
 * @param {Array<string>} [options.fields] - Array of fields to fetch from the place
 * @param {boolean} [options.includeGeometry=true] - Whether to include geometry/location data
 * @param {boolean} [options.includeAddress=true] - Whether to include address components
 * @param {boolean} [options.includeFormatted=true] - Whether to include formatted address
 * @param {boolean} [options.includeDisplay=true] - Whether to include display name
 * @returns {Promise<Object>} Promise that resolves to the place object with requested fields
 * @throws {Error} Throws error if place retrieval fails
 */
async function fetchPlaceFromPrediction(placePrediction, options = {}) {
    const settings = {
        fields: options.fields || null,
        includeGeometry: options.includeGeometry !== false,
        includeAddress: options.includeAddress !== false,
        includeFormatted: options.includeFormatted !== false,
        includeDisplay: options.includeDisplay !== false
    };

    try {
        // Convert prediction to place object
        const place = placePrediction.toPlace();

        // Build fields array based on options
        let fieldsToFetch;
        if (settings.fields) {
            fieldsToFetch = settings.fields;
        } else {
            fieldsToFetch = [];
            if (settings.includeDisplay) fieldsToFetch.push("displayName");
            if (settings.includeFormatted) fieldsToFetch.push("formattedAddress");
            if (settings.includeGeometry) fieldsToFetch.push("location");
            if (settings.includeAddress) fieldsToFetch.push("addressComponents");
        }

        // Fetch the specified fields
        await place.fetchFields({
            fields: fieldsToFetch
        });

        return place;
    } catch (error) {
        console.error('Error fetching place from prediction:', error);
        throw new Error(`Failed to fetch place data: ${error.message}`);
    }
}

/**
 * Retrieves multiple places from an array of place predictions
 * @param {Array<Object>} placePredictions - Array of Google Maps place prediction objects
 * @param {Object} options - Same configuration options as fetchPlaceFromPrediction
 * @returns {Promise<Array<Object>>} Promise that resolves to array of place objects
 * @throws {Error} Throws error if any place retrieval fails
 */
async function fetchPlacesFromPredictions(placePredictions, options = {}) {
    try {
        const placePromises = placePredictions.map(prediction =>
            fetchPlaceFromPrediction(prediction, options)
        );

        return await Promise.all(placePromises);
    } catch (error) {
        console.error('Error fetching places from predictions:', error);
        throw new Error(`Failed to fetch places data: ${error.message}`);
    }
}

/**
 * Fetches autocomplete suggestions from Google Places API with customizable parameters
 * @param {string} inputValue - The search input text
 * @param {Object} options - Configuration options for the request
 * @param {string} [options.sessionToken] - Session token for billing optimization
 * @param {Array<string>} [options.includedRegionCodes=['IT']] - Array of country codes to include
 * @param {Object} [options.locationBias] - Location bias configuration
 * @param {Object} [options.locationBias.center] - Center point for location bias
 * @param {number} [options.locationBias.center.lat] - Latitude of center point
 * @param {number} [options.locationBias.center.lng] - Longitude of center point
 * @param {number} [options.locationBias.radius] - Radius in meters for location bias
 * @param {Array<string>} [options.types] - Place types to include (e.g., ['geocode'], ['establishment'])
 * @param {number} [options.maxResults] - Maximum number of results to return
 * @param {Object} [options.componentRestrictions] - Component restrictions (deprecated, use includedRegionCodes)
 * @returns {Promise<Object>} Promise resolving to { suggestions: Array } from Google Places API
 * @throws {Error} If the API request fails
 * 
 * @example
 * // Basic usage for address autocomplete
 * const result = await fetchAutocompleteSuggestions("Via Roma", {
 *   includedRegionCodes: ["IT"],
 *   locationBias: {
 *     center: { lat: 41.9028, lng: 12.4964 },
 *     radius: 50000
 *   }
 * });
 * 
 * @example
 * // Store locator with geocode types only
 * const result = await fetchAutocompleteSuggestions("Milano", {
 *   types: ['geocode'],
 *   includedRegionCodes: ["IT"],
 *   locationBias: {
 *     center: { lat: 45.4642, lng: 9.1900 },
 *     radius: 100000
 *   }
 * });
 * 
 * @example
 * // Restaurant search with establishment types
 * const result = await fetchAutocompleteSuggestions("pizzeria", {
 *   types: ['establishment'],
 *   includedRegionCodes: ["IT"],
 *   maxResults: 10
 * });
 */
async function fetchAutocompleteSuggestions(inputValue, options = {}) {
    try {
        // Import Google Maps libraries if not already available
        const { AutocompleteSuggestion } = await google.maps.importLibrary("places");
        
        // Create session token if not provided
        const sessionToken = options.sessionToken || new google.maps.places.AutocompleteSessionToken();
        
        // Default configuration
        const settings = {
            includedRegionCodes: options.includedRegionCodes || ['IT'],
            locationBias: options.locationBias || {
                center: { lat: 41.9028, lng: 12.4964 }, // Default to Rome, Italy
                radius: 50000 // Default 50km radius
            },
            types: options.types || undefined, // No type restriction by default
            maxResults: options.maxResults || undefined,
            ...options
        };

        // Build the request object
        const request = {
            input: inputValue,
            sessionToken: sessionToken,
            includedRegionCodes: settings.includedRegionCodes,
            locationBias: settings.locationBias
        };

        // Add optional parameters if provided
        if (settings.types) {
            request.types = settings.types;
        }
        
        if (settings.maxResults) {
            request.maxResults = settings.maxResults;
        }

        // Support legacy componentRestrictions for backward compatibility
        if (settings.componentRestrictions && !options.includedRegionCodes) {
            if (settings.componentRestrictions.country) {
                request.includedRegionCodes = Array.isArray(settings.componentRestrictions.country) 
                    ? settings.componentRestrictions.country 
                    : [settings.componentRestrictions.country];
            }
        }

        // Fetch suggestions from Google Places API
        const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        
        return result;
        
    } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
        throw new Error(`Failed to fetch autocomplete suggestions: ${error.message}`);
    }
}

/**
 * Updates the visual selection and ARIA attributes for suggestion items
 * @param {jQuery} $items - jQuery collection of suggestion items
 * @param {number} selectedIndex - Index of the item to select
 * @param {jQuery} $input - The input element to update ARIA attributes on
 * @param {jQuery} $container - The scrollable container for the suggestions
 * @param {Object} options - Configuration options
 * @param {string} [options.focusClass='cc-focus'] - CSS class for focused items
 * @param {boolean} [options.autoScroll=true] - Whether to auto-scroll selected item into view
 * @param {boolean} [options.updateInputValue=true] - Whether to update input value when navigating
 * @param {Array} [options.suggestions] - Array of suggestion objects
 * @param {Function} [options.textExtractor] - Function to extract text from suggestion
 */
function updateSelectedSuggestion($items, selectedIndex, $input, $container, options = {}) {
    const settings = {
        focusClass: options.focusClass || 'cc-focus',
        autoScroll: options.autoScroll !== false,
        updateInputValue: options.updateInputValue !== false,
        suggestions: options.suggestions || [],
        textExtractor: options.textExtractor || ((suggestion) => suggestion.placePrediction?.text?.text || suggestion.text || '')
    };

    $items.each((index, item) => {
        const $item = $(item);
        const isSelected = index === selectedIndex;
        
        $item.toggleClass(settings.focusClass, isSelected);
        $item.attr('aria-selected', isSelected ? 'true' : 'false');
        
        if (isSelected) {
            updateAutocompleteState($input, { 
                expanded: true,
                activeDescendant: $item.attr('id')
            });
            
            // Update input value with selected suggestion text
            if (settings.updateInputValue && settings.suggestions[selectedIndex]) {
                const suggestionText = settings.textExtractor(settings.suggestions[selectedIndex]);
                $input.val(suggestionText);
            }
            
            // Auto-scroll selected item into view if enabled
            if (settings.autoScroll && $container) {
                scrollItemIntoView($item, $container);
            }
        }
    });
}

/**
 * Scrolls a suggestion item into view within its container
 * @param {jQuery} $item - The item to scroll into view
 * @param {jQuery} $container - The scrollable container
 */
function scrollItemIntoView($item, $container) {
    const containerTop = $container.scrollTop();
    const containerHeight = $container.height();
    const itemTop = $item.position().top;
    const itemHeight = $item.outerHeight();
    
    if (itemTop < 0) {
        $container.scrollTop(containerTop + itemTop);
    } else if (itemTop + itemHeight > containerHeight) {
        $container.scrollTop(containerTop + itemTop - containerHeight + itemHeight);
    }
}

/**
 * Handles keyboard navigation for autocomplete suggestions
 * @param {Event} event - The keyboard event
 * @param {Object} context - Context object containing necessary elements and data
 * @param {jQuery} context.$input - The input element
 * @param {jQuery} context.$container - The suggestions container
 * @param {jQuery} context.$suggestionsContainer - The scrollable suggestions container
 * @param {Array} context.currentSuggestions - Array of current suggestion objects
 * @param {Function} context.handleSelection - Function to call when a suggestion is selected
 * @param {Function} context.hideSuggestions - Function to call to hide suggestions
 * @param {Object} options - Configuration options
 * @param {string} [options.itemSelector='.cc-suggestion-item'] - CSS selector for suggestion items
 * @param {string} [options.focusClass='cc-focus'] - CSS class for focused items
 * @param {Function} [options.textExtractor] - Function to extract display text from suggestion
 * @returns {boolean} Returns true if the event was handled, false otherwise
 */
function handleKeyboardNavigation(event, context, options = {}) {
    const settings = {
        itemSelector: options.itemSelector || '.cc-suggestion-item',
        focusClass: options.focusClass || 'cc-focus',
        textExtractor: options.textExtractor || ((suggestion) => suggestion.placePrediction?.text?.text || suggestion.text || '')
    };

    const $suggestionItems = context.$suggestionsContainer.find(settings.itemSelector);
    
    if ($suggestionItems.length === 0) return false;

    let selectedIndex = $suggestionItems.index($suggestionItems.filter(`.${settings.focusClass}`));

    switch (event.key) {
        case "ArrowDown":
            event.preventDefault();
            
            // Save original input value when starting navigation
            if (!context.$input.data('originalValue')) {
                context.$input.data('originalValue', context.$input.val());
            }
            
            selectedIndex = selectedIndex < $suggestionItems.length - 1 ? selectedIndex + 1 : 0;
            updateSelectedSuggestion($suggestionItems, selectedIndex, context.$input, context.$suggestionsContainer, {
                focusClass: settings.focusClass,
                suggestions: context.currentSuggestions,
                textExtractor: settings.textExtractor
            });
            return true;

        case "ArrowUp":
            event.preventDefault();
            
            // Save original input value when starting navigation
            if (!context.$input.data('originalValue')) {
                context.$input.data('originalValue', context.$input.val());
            }
            
            selectedIndex = selectedIndex > 0 ? selectedIndex - 1 : $suggestionItems.length - 1;
            updateSelectedSuggestion($suggestionItems, selectedIndex, context.$input, context.$suggestionsContainer, {
                focusClass: settings.focusClass,
                suggestions: context.currentSuggestions,
                textExtractor: settings.textExtractor
            });
            return true;

        case "Enter":
            event.preventDefault();
            if (selectedIndex >= 0 && context.currentSuggestions[selectedIndex]) {
                context.handleSelection(context.currentSuggestions[selectedIndex], context.$input.val());
                context.hideSuggestions();
            }
            // Clear the stored original value after selection
            context.$input.removeData('originalValue');
            return true;

        case "Escape":
            context.hideSuggestions();
            return true;

        case "Tab":
            // Clear the stored original value without restoring (keep current value)
            context.$input.removeData('originalValue');
            context.hideSuggestions();
            return true;

        default:
            return false;
    }
}

/**
 * Initializes Google Maps autocomplete with proper validation and error handling
 * @param {Function} initCallback - Function to call when initialization is successful
 * @param {Object} options - Configuration options
 * @param {jQuery} [options.$form] - Form element for fallback error handling
 * @param {Function} [options.onError] - Custom error handler function
 */
function initializeGoogleMapsAutocomplete(initCallback, options = {}) {
    const settings = {
        $form: options.$form || null,
        onError: options.onError || function(message) { console.error(message); }
    };

    function initializeWhenReady() {
        const googleApisAutocomplete = extractGoogleMapsApiKey();
        
        if (googleApisAutocomplete && googleApisAutocomplete.trim() !== 'null') {
            isGoogleApiKeyValid(googleApisAutocomplete, function (isValid) {
                if (isValid) {
                    initCallback();
                } else {
                    settings.onError("Google API Key is not valid for Places Autocomplete. Autocomplete is disabled.");
                    if (settings.$form && settings.$form.length > 0) {
                        settings.$form.on('submit', function () {
                            console.error("Form is being submitted without autocomplete.");
                        });
                    }
                }
            });
        } else {
            settings.onError("Google API Key is null or not available. Autocomplete is disabled.");
        }
    }

    // Expose initialization function globally for async callback
    window.initializeGoogleAutocomplete = function() {
        initializeWhenReady();
    };

    // Handle different loading scenarios
    if (window.googleMapsLoaded && typeof google !== 'undefined' && google.maps && google.maps.places) {
        // API already loaded via async callback
        initializeWhenReady();
    } else if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        // API loaded but callback not yet called
        initializeWhenReady();
    }
    // If neither condition is met, wait for the async callback
}

module.exports = {
    createAutocompleteContainer,
    createLiveRegion,
    updateAutocompleteState,
    announceResults,
    createSuggestionItem,
    createSuggestionItems,
    updateSelectedSuggestion,
    scrollItemIntoView,
    handleKeyboardNavigation,
    extractGoogleMapsApiKey,
    isGoogleApiKeyValid,
    fetchPlaceFromPrediction,
    fetchPlacesFromPredictions,
    fetchAutocompleteSuggestions,
    initializeGoogleMapsAutocomplete,
    AUTOCOMPLETE_CONFIG
};